import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PricingTerm } from '@/constants/pricingTypes';
import { QuoteState, QuoteAttribute } from '@/schemas/quoteResponseSchema';
import { ErrorResponse } from '@/schemas/errorResponseSchema';
import { StepPriceData } from '@/schemas/sceneDataSchema';

interface QuoteStore {
  quote: QuoteState | null;
  showLoader: boolean;
  error: { data?: ErrorResponse; status?: number } | null;
  setError: (error: { data?: ErrorResponse; status?: number } | null) => void;
  setQuote: (quote: QuoteState) => void;
  setShowLoader: (showLoader: boolean) => void;
  updateQuoteAttributes: (
    symbol: string,
    scope: string,
    updater: (attrs: QuoteAttribute[]) => QuoteAttribute[]
  ) => void;
  updatePricingTerm: (term: PricingTerm) => void;
  // Hydrates pricing and coverage data from step_data returned by the server on every
  // GET/POST /quote and PUT /steps/:step_id response. Either argument may be null if
  // the server did not include that data for the current step.
  hydrateFromStepData: (coverages: QuoteState['coverages'] | null, priceData: StepPriceData | null) => void;
}

export const useQuoteStore = create<QuoteStore>()(
  devtools(
    (set) => ({
      quote: null,
      showLoader: false,
      error: null,

      setQuote: (quote) => set({ quote }, false, 'setQuote'),
      setShowLoader: (showLoader) => set({ showLoader }, false, 'setShowLoader'),
      setError: (error) => set({ error }, false, 'setError'),

      updateQuoteAttributes: (symbol, scope, updater) =>
        set(
          (state) => {
            if (!state.quote) return state;
            const coverageIndex = state.quote.coverages.findIndex(
              (coverage) => coverage.symbol === symbol && coverage.scope === scope
            );
            if (coverageIndex === -1) return state;

            return {
              quote: {
                ...state.quote,
                coverages: state.quote.coverages.map((coverage, index) =>
                  index === coverageIndex ? { ...coverage, attributes: updater(coverage.attributes) } : coverage
                ),
              },
            };
          },
          false,
          'updateQuoteAttributes'
        ),

      updatePricingTerm: (term) =>
        set(
          (state) => {
            if (!state.quote) return state;
            return {
              quote: {
                ...state.quote,
                details: { ...state.quote.details, initial_term_selection: term },
              },
            };
          },
          false,
          'updatePricingTerm'
        ),

      hydrateFromStepData: (coverages, priceData) =>
        set(
          (state) => {
            if (!coverages && !priceData) return state;

            const current = state.quote;

            // First load: initialize as soon as price_data arrives; coverages defaults to []
            // until coverage_selections hydrates the full array.
            if (!current) {
              if (!priceData) return state;
              return {
                quote: {
                  coverages: coverages ?? [],
                  details: priceData.details,
                  invoice_period: priceData.details.initial_term_selection,
                  payment_modal_content: priceData.paymentModalContent,
                },
              };
            }

            // Subsequent loads: merge whichever parts were returned.
            return {
              quote: {
                ...current,
                ...(coverages && { coverages }),
                ...(priceData && {
                  details: priceData.details,
                  invoice_period: priceData.details.initial_term_selection,
                  payment_modal_content: priceData.paymentModalContent,
                }),
              },
            };
          },
          false,
          'hydrateFromStepData'
        ),
    }),
    { name: 'QuoteStore' }
  )
);

// Selectors
export const useQuote = () => useQuoteStore((state) => state.quote);
export const useShowLoader = () => useQuoteStore((state) => state.showLoader);
export const useQuoteError = () => useQuoteStore((state) => state.error);
